// @flow
import { isEmpty, omit } from 'lodash';
import { makeActionCreator, reduce, getIdxById } from '../helpers/utils';
import ApiClient from '../api/Api';
import { notify } from '../helpers/BugsnagClient';

const debug = require('debug')('jp:reducers:payments');

export const INITIALIZE_PAYPAL = 'payments/INITIALIZE_PAYPAL';
export const GET_BRAINTREE_TOKEN = 'payments/GET_BRAINTREE_TOKEN';
export const GET_BRAINTREE_TOKEN_SUCCESS =
  'payments/GET_BRAINTREE_TOKEN_SUCCESS';
export const GET_BRAINTREE_TOKEN_FAIL = 'payments/GET_BRAINTREE_TOKEN_FAIL';
export const BRAINTREE_PAYPAL_FAILED = 'payments/BRAINTREE_PAYPAL_FAILED';
export const BRAINTREE_PAYPAL_INITIALIZED =
  'payments/BRAINTREE_PAYPAL_INITIALIZED';
export const LOAD_PAYMENTMETHODS = 'payments/LOAD_PAYMENTMETHODS';
export const LOAD_PAYMENTMETHODS_SUCCESS =
  'payments/LOAD_PAYMENTMETHODS_SUCCESS';
export const LOAD_PAYMENTMETHODS_FAIL = 'payments/LOAD_PAYMENTMETHODS_FAIL';
export const TOGGLE_USER_ADD_PAYMENTMETHOD =
  'payments/TOGGLE_USER_ADD_PAYMENTMETHOD';
export const CREATE_PAYMENTMETHOD = 'payments/CREATE_PAYMENTMETHOD';
export const CREATE_PAYMENTMETHOD_SUCCESS =
  'payments/CREATE_PAYMENTMETHOD_SUCCESS';
export const CREATE_PAYMENTMETHOD_FAIL = 'payments/CREATE_PAYMENTMETHOD_FAIL';
export const CREATE_PAYPAL_PAYMENTMETHOD =
  'payments/CREATE_PAYPAL_PAYMENTMETHOD';
export const CREATE_PAYPAL_PAYMENTMETHOD_SUCCESS =
  'payments/CREATE_PAYPAL_PAYMENTMETHOD_SUCCESS';
export const CREATE_PAYPAL_PAYMENTMETHOD_FAIL =
  'payments/CREATE_PAYPAL_PAYMENTMETHOD_FAIL';
export const TOGGLE_ALL_PAYMENTMETHODS = 'payments/TOGGLE_ALL_PAYMENTMETHODS';
export const SELECT_PAYMENTMETHOD = 'payments/SELECT_PAYMENTMETHOD';
export const DELETE_PAYMENTMETHOD = 'payments/DELETE_PAYMENTMETHOD';
export const DELETE_PAYMENTMETHOD_SUCCESS =
  'payments/DELETE_PAYMENTMETHOD_SUCCESS';
export const DELETE_PAYMENTMETHOD_FAIL = 'payments/DELETE_PAYMENTMETHOD_FAIL';
export const UPDATE_PAYMENT_TAB = 'payments/UPDATE_PAYMENT_TAB';
export const SET_PAYMENT_DETAILS_LOCK = 'payments/SET_PAYMENT_DETAILS_LOCK';
export const SET_PAYMENT_DETAILS_SUBMITTING =
  'payments/SET_PAYMENT_DETAILS_SUBMITTING';
export const CARD_TOKENIZING = 'payments/CARD_TOKENIZING';
export const ADD_PROMO_CODE = 'payments/ADD_PROMO_CODE';
export const ADD_PROMO_CODE_SUCCESS = 'payments/ADD_PROMO_CODE_SUCCESS';
export const ADD_PROMO_CODE_FAIL = 'payments/ADD_PROMO_CODE_FAIL';
export const FILTER_PAYMENT_METHODS = 'payments/FILTER_PAYMENT_METHODS';
export const HOSTED_FIELDS_ERRORS = 'payments/HOSTED_FIELDS_ERRORS';
export const CLEAR_GLOBAL_API_ERROR = 'payments/CLEAR_GLOBAL_API_ERROR';

export const SET_GOOGLE_PAY_AVAILABLE = 'payments/SET_GOOGLE_PAY_AVAILABLE';
export const SET_GOOGLE_PAY_HAS_INITIALISED =
  'payments/SET_GOOGLE_PAY_HAS_INITIALISED';
export const SET_APPLE_PAY_AVAILABLE = 'payments/SET_APPLE_PAY_AVAILABLE';
export const SET_APPLE_PAY_HAS_INITIALISED =
  'payments/SET_APPLE_PAY_HAS_INITIALISED';
export const SET_INITIAL_STATE = 'payments/SET_INITIAL_STATE';

type Action =
  | {| type: typeof GET_BRAINTREE_TOKEN |}
  | {| type: typeof LOAD_PAYMENTMETHODS |}
  | {| type: typeof LOAD_PAYMENTMETHODS_SUCCESS, payload: any |}
  | {| type: typeof LOAD_PAYMENTMETHODS_FAIL |}
  | {| type: typeof TOGGLE_ALL_PAYMENTMETHODS |}
  | {| type: typeof DELETE_PAYMENTMETHOD, id: number |}
  | {| type: typeof DELETE_PAYMENTMETHOD_SUCCESS, id: number |}
  | {| type: typeof DELETE_PAYMENTMETHOD_FAIL, id: number |}
  | {| type: typeof CREATE_PAYMENTMETHOD |}
  | {| type: typeof CREATE_PAYMENTMETHOD_SUCCESS |}
  | {| type: typeof CREATE_PAYMENTMETHOD_FAIL |}
  | {| type: typeof UPDATE_PAYMENT_TAB, index: number |}
  | {| type: typeof SET_PAYMENT_DETAILS_LOCK, lock: boolean |}
  | {| type: typeof CARD_TOKENIZING |}
  | {| type: typeof SET_PAYMENT_DETAILS_SUBMITTING, submitting: boolean |}
  | {| type: typeof TOGGLE_USER_ADD_PAYMENTMETHOD, bool: boolean |}
  | {| type: typeof ADD_PROMO_CODE |}
  | {| type: typeof ADD_PROMO_CODE_SUCCESS |}
  | {| type: typeof ADD_PROMO_CODE_FAIL |}
  | {|
      type: typeof FILTER_PAYMENT_METHODS,
      excludedPaymentMethods: Array<any>
    |}
  | {| type: typeof HOSTED_FIELDS_ERRORS, errors: any |}
  | {| type: typeof SET_GOOGLE_PAY_AVAILABLE |}
  | {| type: typeof SET_APPLE_PAY_AVAILABLE |}
  | {| type: typeof SET_GOOGLE_PAY_HAS_INITIALISED |}
  | {| type: typeof SET_APPLE_PAY_HAS_INITIALISED |}
  | {| type: typeof HOSTED_FIELDS_ERRORS, errors: any |}
  | {| type: typeof CLEAR_GLOBAL_API_ERROR |}
  | {| type: typeof SET_INITIAL_STATE |};

export type PaymentMethod = {|
  id: number,
  source: string,
  authority: ?string,
  isPrimary: boolean,
  isBlocked: boolean,
  isAuthorised: boolean,
  createdAt: string,
  name: string,
  cardType: string,
  lastFour: ?string,
  expiryDate: string,
  email?: string,
  isActive: boolean
|};

export type State = {|
  braintreeToken: ?string,
  braintreePaypalInitialized: boolean,
  braintreePaypalFailed: boolean,
  braintreePaypalLoading: boolean,
  loadingPaymentMethods: boolean,
  loadPaymentMethodsComplete: boolean,
  paymentMethods: PaymentMethod[],
  creatingPaypalPaymentMethod: boolean,
  deletingPaymentMethod: boolean,
  displayAllPaymentMethods: boolean,
  userAddingPaymentMethod: boolean,
  newPaymentMethodAdded: boolean,
  paymentDetailsLocked: boolean,
  paymentDetailsSubmitting: boolean,
  activePaymentTab: number,
  hostedFieldErrors: {
    'credit-card-number'?: string,
    expiration?: string,
    cvv?: string
  },
  apiErrors: {
    availability: ?string
  },
  globalApiError: ?{
    title: string,
    code?: number
  },
  isGooglePayAvailable: boolean,
  isGooglePayActive: boolean,
  hasGooglePayInitialised: boolean,
  isApplePayAvailable: boolean,
  isApplePayActive: boolean,
  hasApplePayInitialised: boolean
|};

export const initialState: State = {
  apiErrors: {
    availability: null
  },
  braintreeToken: null,
  braintreePaypalInitialized: false,
  braintreePaypalFailed: false,
  braintreePaypalLoading: false,
  loadPaymentMethodsComplete: false,
  loadingPaymentMethods: false,
  paymentMethods: [],
  creatingPaypalPaymentMethod: false,
  hostedFieldErrors: {},
  newPaymentMethodAdded: false,
  paymentDetailsSubmitting: false,
  paymentDetailsLocked: false,
  deletingPaymentMethod: false,
  displayAllPaymentMethods: false,
  activePaymentTab: 0,
  globalApiError: null,
  userAddingPaymentMethod: false, // assumed false as we don't want to trigger network requests for the iframes unless required,
  isGooglePayAvailable: false,
  isGooglePayActive: false,
  isApplePayAvailable: false,
  isApplePayActive: false,
  hasGooglePayInitialised: false,
  hasApplePayInitialised: false
};

export const isMultiUseActive = paymentMethods =>
  !!paymentMethods?.find(el => el.isActive && el.cardType === 'Multi-use');

const booleanKeySort = key => (a, b) => {
  if (a[key]) {
    return -1;
  }
  if (b[key]) {
    return 1;
  }
  return 0;
};
const isActiveSort = booleanKeySort('isActive');
const isPrimarySort = booleanKeySort('isPrimary');
const isGooglePaySort = booleanKeySort('isGooglePay');
const isApplePaySort = booleanKeySort('isApplePay');

const sortPaymentMethods = paymentMethods =>
  paymentMethods
    .sort(isPrimarySort)
    .sort(isGooglePaySort)
    .sort(isApplePaySort)
    .sort(isActiveSort);

const getPrimaryPaymentMethodId = (
  methods: PaymentMethod[],
  onlyPayPal: boolean
): ?number => {
  const results = methods
    .filter(item => (item.source === 'paypal') === onlyPayPal)
    .sort(isPrimarySort);

  return results.length ? results[0].id : null;
};

const getActivePaymentTab = paymentMethods =>
  paymentMethods.length && paymentMethods[0].source === 'paypal' ? 1 : 0;

const getNonPaypalMethods = paymentMethods =>
  paymentMethods.filter(method => method.source !== 'paypal');

const noActivePaymentMethod = paymentMethods =>
  !isEmpty(paymentMethods) &&
  paymentMethods.filter(item => item.isActive).length === 0;

const setActivePaymentMethod = paymentMethods => {
  const withActivePaymentMethod = [...paymentMethods];
  withActivePaymentMethod[0].isActive = true;

  return withActivePaymentMethod;
};

const isPreferentialMethod = (source: string, cardType: string) =>
  source === 'multi_usage' ||
  source === 'resident_ticket' ||
  cardType === 'Corporate';

const includesPreferentialPaymentMethod = paymentMethods =>
  Boolean(
    paymentMethods.filter(item =>
      isPreferentialMethod(item.source, item.cardType)
    ).length
  );

const includesGooglePayMethod = paymentMethods =>
  Boolean(paymentMethods.filter(item => item.id === 'googlePay').length);
const includesApplePayMethod = paymentMethods =>
  Boolean(paymentMethods.filter(item => item.id === 'applePay').length);
const findActivePaymentMethod = paymentMethods =>
  paymentMethods.filter(paymentMethod => paymentMethod.isActive)[0];

const isGoogleOrApplePaymentMethod = (
  id: string,
  hasGooglePayMethod: boolean,
  hasApplePayMethod: boolean
) =>
  (hasApplePayMethod && id === 'applePay') ||
  (hasGooglePayMethod && id === 'googlePay');

/*
Returns if a payment method isActive

If on paypal return isActive if id === primary payment method id
Otherwise:
1. If it is a preferential method
2. If there are no preferential methods, check if there are ApplePay / GooglePay
3. Finally the primary payment method

The payment methods are also sorted in this order.
 */

const isNewPaymentMethodActive = (
  id: string,
  primaryPaymentId: string,
  source: string,
  cardType: string,
  hasPreferentialPaymentMethod: boolean,
  isOnCardTab: boolean,
  hasGooglePayMethod: boolean,
  hasApplePayMethod: boolean
) => {
  if (isOnCardTab && isPreferentialMethod(source, cardType)) {
    return true;
  }

  if (
    isOnCardTab &&
    !hasPreferentialPaymentMethod &&
    isGoogleOrApplePaymentMethod(id, hasGooglePayMethod, hasApplePayMethod)
  ) {
    return true;
  }

  if (
    (isOnCardTab &&
      !hasPreferentialPaymentMethod &&
      !hasGooglePayMethod &&
      !hasApplePayMethod) ||
    !isOnCardTab
  ) {
    return id === primaryPaymentId;
  }

  return false;
};

const filterNewPaymentMethods = (
  paymentMethods: Array<any>,
  primaryPaymentId: string,
  isOnCardTab: boolean
) => {
  if (!paymentMethods.length) {
    return paymentMethods;
  }

  const hasPreferentialPaymentMethod = includesPreferentialPaymentMethod(
    paymentMethods
  );
  const hasGooglePayMethod = includesGooglePayMethod(paymentMethods);
  const hasApplePayMethod = includesApplePayMethod(paymentMethods);

  let hasAnActivePaymentMethod;
  return paymentMethods
    .filter(item => item.cardType && item.cardType.toLowerCase())
    .sort(isPrimarySort)
    .sort(isApplePaySort)
    .map(paymentMethod => {
      const { id, source, cardType } = paymentMethod;

      const isActive = isNewPaymentMethodActive(
        id,
        primaryPaymentId,
        source,
        cardType,
        hasPreferentialPaymentMethod,
        isOnCardTab,
        hasGooglePayMethod,
        hasApplePayMethod
      );

      if (isActive && !hasAnActivePaymentMethod) {
        hasAnActivePaymentMethod = true;

        return { ...paymentMethod, isActive };
      }

      return {
        ...paymentMethod,
        isActive: false
      };
    });
};

export default reduce<State, Action>(initialState, {
  [SET_INITIAL_STATE](): State {
    debug(SET_INITIAL_STATE);
    return {
      ...initialState
    };
  },
  [INITIALIZE_PAYPAL](state: State, action: Action): State {
    debug(INITIALIZE_PAYPAL, action);

    return {
      ...state,
      braintreePaypalLoading: true
    };
  },
  [GET_BRAINTREE_TOKEN_SUCCESS](state: State, action: Action): State {
    debug(GET_BRAINTREE_TOKEN_SUCCESS, action);

    if (action.payload && action.payload.data) {
      return {
        ...state,
        braintreeToken: action.payload.data.token,
        braintreePaypalFailed: false,
        braintreePaypalLoading: false
      };
    }
    return state;
  },
  [GET_BRAINTREE_TOKEN_FAIL](state: State, action: Action): State {
    debug(GET_BRAINTREE_TOKEN_FAIL, action);

    return {
      ...state,
      braintreePaypalFailed: true,
      braintreePaypalLoading: false
    };
  },
  [BRAINTREE_PAYPAL_FAILED](state: State, action: Action): State {
    debug(BRAINTREE_PAYPAL_FAILED, action);

    return {
      ...state,
      braintreePaypalFailed: true,
      braintreePaypalLoading: false
    };
  },
  [BRAINTREE_PAYPAL_INITIALIZED](state: State, action: Action): State {
    debug(BRAINTREE_PAYPAL_INITIALIZED, action);

    return {
      ...state,
      braintreePaypalInitialized: action.initialized
    };
  },
  [LOAD_PAYMENTMETHODS](state: State): State {
    debug(LOAD_PAYMENTMETHODS);
    return { ...state, loadingPaymentMethods: true };
  },
  [LOAD_PAYMENTMETHODS_SUCCESS](state: State, action: Action): State {
    debug(LOAD_PAYMENTMETHODS_SUCCESS, action);

    if (action.payload && action.payload.data) {
      const rawPaymentMethods = action.payload.data;
      const { activePaymentTab: currentActivePaymentTab } = state;
      const allPaymentMethods = [...rawPaymentMethods, ...state.paymentMethods];

      const paymentMethods = filterNewPaymentMethods(
        allPaymentMethods,
        rawPaymentMethods[0]?.id,
        currentActivePaymentTab === 0
      );

      const sortedPaymentMethods = sortPaymentMethods(paymentMethods);
      const activePaymentTab = getActivePaymentTab(sortedPaymentMethods);
      const nonPaypalMethods = getNonPaypalMethods(sortedPaymentMethods);
      const newActivePaymentMethod = findActivePaymentMethod(
        sortedPaymentMethods
      );
      const tabType = activePaymentTab === 0 ? 'card' : 'paypal';

      return {
        ...state,
        loadingPaymentMethods: false,
        loadPaymentMethodsComplete: true,
        paymentMethods: noActivePaymentMethod(sortedPaymentMethods)
          ? [...setActivePaymentMethod(sortedPaymentMethods)]
          : [...sortedPaymentMethods],
        activePaymentTab,
        paymentDetailsLocked: !isEmpty(paymentMethods),
        userAddingPaymentMethod:
          nonPaypalMethods.length <= 0 && activePaymentTab === 0,
        isGooglePayActive:
          tabType !== 'paypal' &&
          newActivePaymentMethod &&
          newActivePaymentMethod.id === 'googlePay',
        isApplePayActive:
          tabType !== 'paypal' &&
          newActivePaymentMethod &&
          newActivePaymentMethod.id === 'applePay'
      };
    }

    // Notify error to bugsnag
    notify(new Error('No payment methods in checkout get payment methods'), {
      metaData: action
    });

    return {
      ...state,
      loadingPaymentMethods: false,
      loadPaymentMethodsComplete: true,
      paymentMethods: [],
      activePaymentTab: 0,
      paymentDetailsLocked: false,
      userAddingPaymentMethod: true
    };
  },
  [LOAD_PAYMENTMETHODS_FAIL](state: State, action: Action): State {
    debug(LOAD_PAYMENTMETHODS_FAIL, action);
    return {
      ...state,
      loadingPaymentMethods: false,
      loadPaymentMethodsComplete: false
    };
  },
  [FILTER_PAYMENT_METHODS](state: State, action: Action): State {
    debug(FILTER_PAYMENT_METHODS, action);
    if (
      'excludedPaymentMethods' in action &&
      action.excludedPaymentMethods.length
    ) {
      const { paymentMethods } = state;

      const excludedLower = action.excludedPaymentMethods.map(el =>
        el.toLowerCase()
      );

      const filteredPaymentMethods = paymentMethods.filter(el => {
        const allowedByType = !excludedLower.includes(
          el.cardType && el.cardType.toLowerCase()
        );
        const allowedBySource = !excludedLower.includes(
          el.source && el.source.toLowerCase()
        );

        return allowedByType && allowedBySource;
      });
      const activePaymentTab = getActivePaymentTab(filteredPaymentMethods);
      const nonPaypalMethods = getNonPaypalMethods(filteredPaymentMethods);

      const newActivePaymentMethod = filteredPaymentMethods.filter(
        paymentMethod => paymentMethod.isActive
      )[0];

      const excludesGoogle = !!excludedLower.filter(
        el => el === 'google pay' || el === 'google_pay'
      ).length;
      const excludesApple = !!excludedLower.filter(
        el => el === 'apple pay' || el === 'apple_pay'
      ).length;

      const newPaymentMethods = noActivePaymentMethod(filteredPaymentMethods)
        ? [...setActivePaymentMethod(filteredPaymentMethods)]
        : [...filteredPaymentMethods];

      return {
        ...state,
        paymentDetailsLocked:
          newPaymentMethods.length === 0 ? false : state.paymentDetailsLocked,
        paymentMethods: newPaymentMethods,
        activePaymentTab,
        userAddingPaymentMethod:
          nonPaypalMethods.length <= 0 && activePaymentTab === 0,
        isGooglePayActive:
          newActivePaymentMethod && newActivePaymentMethod.id === 'googlePay',
        isApplePayActive:
          newActivePaymentMethod && newActivePaymentMethod.id === 'applePay',
        isGooglePayAvailable: excludesGoogle
          ? false
          : state.isGooglePayAvailable,
        isApplePayAvailable: excludesApple ? false : state.isApplePayAvailable
      };
    }
    return state;
  },
  [SELECT_PAYMENTMETHOD](state: State, action: Action): State {
    debug(SELECT_PAYMENTMETHOD, action);
    if (action && action.id) {
      const { id } = action;
      const { paymentMethods } = state;
      const newPaymentMethods = [
        ...paymentMethods.map(paymentMethod => ({
          ...paymentMethod,
          isActive: paymentMethod.id === id
        }))
      ];

      return {
        ...state,
        paymentMethods: newPaymentMethods,
        isGooglePayActive: id === 'googlePay',
        isApplePayActive: id === 'applePay'
      };
    }
    return state;
  },
  [DELETE_PAYMENTMETHOD](state: State): State {
    debug(DELETE_PAYMENTMETHOD);
    return { ...state, deletingPaymentMethod: true };
  },
  [DELETE_PAYMENTMETHOD_SUCCESS](state: State, action: Action): State {
    debug(DELETE_PAYMENTMETHOD_SUCCESS, action);
    if (action && action.id) {
      const { paymentMethods } = state;
      let newPaymentMethods = [...paymentMethods];
      const idx = getIdxById(paymentMethods, action.id);

      newPaymentMethods.splice(idx, 1);

      // If the item we deleted was active set a new active method item
      if (paymentMethods[idx].isActive && newPaymentMethods.length >= 1) {
        const isPayPalTab = state.activePaymentTab === 1;
        const newActiveId = getPrimaryPaymentMethodId(
          newPaymentMethods,
          isPayPalTab
        );

        newPaymentMethods = newPaymentMethods.map(method => ({
          ...method,
          isActive: method.id === newActiveId
        }));
      }

      const nonPaypalMethods = newPaymentMethods.filter(
        item => item.source !== 'paypal'
      );

      const newActivePaymentMethod = newPaymentMethods.filter(
        paymentMethod => paymentMethod.isActive
      )[0];

      return {
        ...state,
        paymentMethods: sortPaymentMethods(newPaymentMethods),
        deletingPaymentMethod: false,
        userAddingPaymentMethod:
          state.activePaymentTab === 1 ? false : nonPaypalMethods.length === 0,
        isGooglePayActive:
          newActivePaymentMethod && newActivePaymentMethod.id === 'googlePay',
        isApplePayActive:
          newActivePaymentMethod && newActivePaymentMethod.id === 'applePay'
      };
    }

    notify(new Error('No payment method id in delete action'));

    return {
      ...state,
      globalApiError: {
        title: 'Something went wrong trying to delete your payment source'
      },
      deletingPaymentMethod: false
    };
  },
  [DELETE_PAYMENTMETHOD_FAIL](state: State, action: Action): State {
    debug(DELETE_PAYMENTMETHOD_FAIL, action);

    if (action.payload && action.payload.data) {
      return {
        ...state,
        globalApiError: {
          title: action.payload.data.title
        },
        deletingPaymentMethod: false
      };
    }

    return {
      ...state,
      globalApiError: {
        title: 'Something went wrong trying to delete your payment source'
      },
      deletingPaymentMethod: false
    };
  },
  [TOGGLE_ALL_PAYMENTMETHODS](state: State): State {
    debug(TOGGLE_ALL_PAYMENTMETHODS);
    return {
      ...state,
      displayAllPaymentMethods: !state.displayAllPaymentMethods
    };
  },
  [TOGGLE_USER_ADD_PAYMENTMETHOD](state: State, action: Action): State {
    debug(TOGGLE_USER_ADD_PAYMENTMETHOD, action);
    if (action && 'bool' in action) {
      const primaryMethodId = getPrimaryPaymentMethodId(
        [...state.paymentMethods],
        false
      );

      // If user is adding a payment method mark all other methods as not active
      // Otherwise mark the first non paypal primary method as active
      const paymentMethods = action.bool
        ? [...state.paymentMethods].map(method => ({
            ...method,
            isActive: false
          }))
        : [...state.paymentMethods].map(method => ({
            ...method,
            isActive: primaryMethodId === method.id
          }));

      return {
        ...state,
        userAddingPaymentMethod: action.bool,
        paymentMethods
      };
    }

    return {
      ...state
    };
  },
  [SET_PAYMENT_DETAILS_SUBMITTING](state: State, action: Action): State {
    debug(SET_PAYMENT_DETAILS_SUBMITTING);

    if (typeof action.submitting === 'boolean') {
      return {
        ...state,
        paymentDetailsSubmitting: action.submitting
      };
    }

    return {
      ...state
    };
  },
  [SET_PAYMENT_DETAILS_LOCK](state: State, action: Action): State {
    debug(SET_PAYMENT_DETAILS_LOCK);

    if (typeof action.lock === 'boolean') {
      return {
        ...state,
        paymentDetailsLocked: action.lock,
        paymentMethods: sortPaymentMethods(state.paymentMethods)
      };
    }

    return {
      ...state
    };
  },
  [HOSTED_FIELDS_ERRORS](state: State, action: Action): State {
    debug(HOSTED_FIELDS_ERRORS, action);
    if (action && action.errors) {
      return {
        ...state,
        hostedFieldErrors: action.errors
      };
    }

    notify(new Error('No errors in hosted fields errors action'), {
      metaData: action
    });

    return {
      ...state,
      globalApiError: {
        title: 'There has been an error'
      }
    };
  },
  [CARD_TOKENIZING](state: State): State {
    debug(CARD_TOKENIZING);

    // When adding a new card they payment source is added, payment details locked and the primary submit and pay button then becomes active
    // However becuase the payment method ID has changed we call off to get a new booking summary, this causes the pay button to become disabled again
    // This causes a weird looking quick transition from enabled -> disabled -> enabled we want to get rid of.
    // newPaymentMethodAdded is stored here so we can keep that button disabled until the summary endpoint clears it

    return {
      ...state,
      newPaymentMethodAdded: true
    };
  },
  [CREATE_PAYMENTMETHOD](state: State): State {
    debug(CREATE_PAYMENTMETHOD);
    // clear our any apiError we previously received from the api trying to create a payment method
    const apiErrors = omit(state.apiErrors, ['paymentMethodId']);
    return { ...state, paymentDetailsSubmitting: true, apiErrors };
  },
  [CREATE_PAYMENTMETHOD_SUCCESS](state: State, action: Action): State {
    debug(CREATE_PAYMENTMETHOD_SUCCESS, action);
    if (action.payload && action.payload.data) {
      const apiErrors = omit(state.apiErrors, ['paymentMethodId']);

      const newItem = action.payload.data;
      const isPayPalTab = state.activePaymentTab === 1;
      const isPayPalCard = newItem.source === 'paypal';

      const newPaymentMethods = [newItem, ...state.paymentMethods].map(
        paymentMethod => ({
          ...paymentMethod,
          // Set the new item as active if we are showing the tab this method belongs in
          isActive:
            isPayPalTab === isPayPalCard && paymentMethod.id === newItem.id
        })
      );

      return {
        ...state,
        apiErrors,
        paymentMethods: newPaymentMethods,
        userAddingPaymentMethod: false,
        paymentDetailsSubmitting: false,
        // Lock payment section if we have added a new payment method for the tab the user is seeing
        paymentDetailsLocked: isPayPalTab === isPayPalCard,
        hostedFieldErrors: {},
        isGooglePayActive: false,
        isApplePayActive: false
      };
    }

    notify(new Error('No payment method in response'), {
      metaData: action
    });

    return {
      ...state,
      paymentDetailsSubmitting: false
    };
  },
  [CREATE_PAYMENTMETHOD_FAIL](state: State, action: Action): State {
    debug(CREATE_PAYMENTMETHOD_FAIL, action);

    if (action.payload && action.payload.data) {
      return {
        ...state,
        globalApiError: action.payload.data,
        paymentDetailsSubmitting: false
      };
    }
    return {
      ...state,
      globalApiError: {
        title: 'Error creating payment method'
      },
      paymentDetailsSubmitting: false
    };
  },
  [CREATE_PAYPAL_PAYMENTMETHOD](state: State): State {
    debug(CREATE_PAYPAL_PAYMENTMETHOD);
    return {
      ...state,
      creatingPaypalPaymentMethod: true
    };
  },
  [CREATE_PAYPAL_PAYMENTMETHOD_SUCCESS](state: State, action: Action): State {
    debug(CREATE_PAYPAL_PAYMENTMETHOD_SUCCESS);

    if (action.payload && action.payload.data) {
      const newPaymentMethods = [
        ...state.paymentMethods,
        action.payload.data
      ].map(paymentMethod => ({
        ...paymentMethod,
        isActive:
          paymentMethod.id === action.payload.data.id &&
          state.activePaymentTab === 1
      }));

      return {
        ...state,
        newPaymentMethodAdded: true,
        paymentDetailsLocked: state.activePaymentTab === 1,
        paymentMethods: newPaymentMethods,
        creatingPaypalPaymentMethod: false
      };
    }

    notify(new Error('No payment source in response'), {
      metaData: action
    });

    return {
      ...state,
      creatingPaypalPaymentMethod: false,
      globalApiError: {
        code: 100000,
        title: 'Something went wrong trying to add your paypal payment source.'
      }
    };
  },
  [CREATE_PAYPAL_PAYMENTMETHOD_FAIL](state: State) {
    return {
      ...state,
      creatingPaypalPaymentMethod: false,
      globalApiError: {
        code: 100000,
        title: 'Something went wrong trying to add your paypal payment source.'
      }
    };
  },
  [UPDATE_PAYMENT_TAB](state: State, action: Action): State {
    debug(UPDATE_PAYMENT_TAB, action);

    if ('index' in action) {
      if (action.index === state.activePaymentTab) {
        return {
          ...state
        };
      }

      const tabType = action.index === 0 ? 'card' : 'paypal';
      const { paymentMethods } = state;

      const primaryPaymentMethodId = getPrimaryPaymentMethodId(
        [...paymentMethods],
        action.index === 1
      );

      const newPaymentMethods = filterNewPaymentMethods(
        paymentMethods,
        primaryPaymentMethodId,
        tabType === 'card'
      );

      const nonPaypalMethods = paymentMethods.filter(
        method => method.source !== 'paypal'
      );

      const newActivePaymentMethod = findActivePaymentMethod(newPaymentMethods);

      return {
        ...state,
        activePaymentTab: action.index,
        paymentMethods: sortPaymentMethods(newPaymentMethods),
        // If on paypal close the add new card form
        userAddingPaymentMethod:
          tabType === 'paypal' ? false : nonPaypalMethods.length === 0,
        isGooglePayActive:
          tabType !== 'paypal' &&
          newActivePaymentMethod &&
          newActivePaymentMethod.id === 'googlePay',
        isApplePayActive:
          tabType !== 'paypal' &&
          newActivePaymentMethod &&
          newActivePaymentMethod.id === 'applePay'
      };
    }

    return {
      ...state
    };
  },
  [CLEAR_GLOBAL_API_ERROR](state: State): State {
    debug(CLEAR_GLOBAL_API_ERROR);
    return {
      ...state,
      globalApiError: null
    };
  },
  [SET_GOOGLE_PAY_AVAILABLE](state: State) {
    debug(SET_GOOGLE_PAY_AVAILABLE);

    if (state.isGooglePayAvailable || state.isApplePayActive) {
      return { ...state, hasGooglePayInitialised: true };
    }

    const { activePaymentTab } = state;

    const googlePaymentItem = {
      id: 'googlePay',
      cardType: 'Google Pay',
      source: 'google_pay',
      isPrimary: true,
      isActive: true,
      name: 'GOOGLE PAY',
      isGooglePay: true
    };

    const paymentMethods = [...state.paymentMethods];
    const tabType = activePaymentTab === 0 ? 'card' : 'paypal';
    const isPayPalTab = activePaymentTab === 1;

    const primaryPaymentMethodId = getPrimaryPaymentMethodId(
      paymentMethods,
      isPayPalTab
    );
    const newPaymentMethods = filterNewPaymentMethods(
      [...paymentMethods, googlePaymentItem],
      primaryPaymentMethodId,
      tabType === 'card'
    );

    const newActivePaymentMethod = findActivePaymentMethod(newPaymentMethods);

    return {
      ...state,
      paymentDetailsLocked:
        state.loadPaymentMethodsComplete && state.hasApplePayInitialised
          ? true
          : state.paymentDetailsLocked,
      isGooglePayAvailable: true,
      isGooglePayActive:
        tabType !== 'paypal' &&
        newActivePaymentMethod &&
        newActivePaymentMethod.id === 'googlePay',
      isApplePayActive:
        tabType !== 'paypal' &&
        newActivePaymentMethod &&
        newActivePaymentMethod.id === 'applePay',
      hasGooglePayInitialised: true,
      paymentMethods: sortPaymentMethods(newPaymentMethods),
      userAddingPaymentMethod: false
    };
  },
  [SET_GOOGLE_PAY_HAS_INITIALISED](state: State) {
    debug(SET_GOOGLE_PAY_HAS_INITIALISED);

    return {
      ...state,
      hasGooglePayInitialised: true
    };
  },
  [SET_APPLE_PAY_HAS_INITIALISED](state: State) {
    debug(SET_APPLE_PAY_HAS_INITIALISED);

    return {
      ...state,
      hasApplePayInitialised: true
    };
  },
  [SET_APPLE_PAY_AVAILABLE](state: State) {
    debug(SET_APPLE_PAY_AVAILABLE);

    if (state.isApplePayAvailable) {
      return { ...state, hasApplePayInitialised: true };
    }

    const { activePaymentTab } = state;

    const applePaymentItem = {
      id: 'applePay',
      cardType: 'Apple Pay',
      source: 'apple_pay',
      isPrimary: true,
      isActive: true,
      name: 'APPLE PAY',
      isApplePay: true
    };

    const paymentMethods = [...state.paymentMethods];
    const tabType = activePaymentTab === 0 ? 'card' : 'paypal';
    const isPayPalTab = activePaymentTab === 1;

    const primaryPaymentMethodId = getPrimaryPaymentMethodId(
      paymentMethods,
      isPayPalTab
    );

    const newPaymentMethods = filterNewPaymentMethods(
      [...paymentMethods, applePaymentItem],
      primaryPaymentMethodId,
      tabType === 'card'
    );

    const newActivePaymentMethod = findActivePaymentMethod(newPaymentMethods);

    return {
      ...state,
      paymentDetailsLocked:
        state.loadPaymentMethodsComplete && state.hasGooglePayInitialised
          ? true
          : state.paymentDetailsLocked,
      isApplePayAvailable: true,
      isApplePayActive:
        tabType !== 'paypal' &&
        newActivePaymentMethod &&
        newActivePaymentMethod.id === 'applePay',
      hasApplePayInitialised: true,
      isGooglePayActive:
        tabType !== 'paypal' &&
        newActivePaymentMethod &&
        newActivePaymentMethod.id === 'googlePay',
      paymentMethods: sortPaymentMethods(newPaymentMethods),
      userAddingPaymentMethod: false
    };
  }
});

export function getBraintreeToken() {
  return {
    types: [
      GET_BRAINTREE_TOKEN,
      GET_BRAINTREE_TOKEN_SUCCESS,
      GET_BRAINTREE_TOKEN_FAIL
    ],
    promise: (client: ApiClient) => client.paymentToken.braintree()
  };
}

export const setBraintreePaypalInitialized = makeActionCreator(
  BRAINTREE_PAYPAL_INITIALIZED,
  'initialized'
);

export const selectPaymentMethod = makeActionCreator(
  SELECT_PAYMENTMETHOD,
  'id'
);

export const toggleUserAddPaymentMethod = makeActionCreator(
  TOGGLE_USER_ADD_PAYMENTMETHOD,
  'bool'
);

export function loadPaymentMethods(listingId?: number) {
  return {
    types: [
      LOAD_PAYMENTMETHODS,
      LOAD_PAYMENTMETHODS_SUCCESS,
      LOAD_PAYMENTMETHODS_FAIL
    ],
    promise: (client: ApiClient) =>
      client.paymentMethods.getPaymentMethods(listingId)
  };
}

export const setPaymentDetailsLock = makeActionCreator(
  SET_PAYMENT_DETAILS_LOCK,
  'lock'
);

export const setPaymentDetailsSubmitting = makeActionCreator(
  SET_PAYMENT_DETAILS_SUBMITTING,
  'submitting'
);

export const handleHostedFieldsErrors = makeActionCreator(
  HOSTED_FIELDS_ERRORS,
  'errors'
);

export const setCardTokenizing = makeActionCreator(CARD_TOKENIZING);

export function createPaymentMethod(nonce: string, model: any) {
  return (dispatch: (*) => Promise<*>) => {
    const { postalCode } = model;

    return new Promise<*>((resolve, reject) => {
      dispatch({
        types: [
          CREATE_PAYMENTMETHOD,
          CREATE_PAYMENTMETHOD_SUCCESS,
          CREATE_PAYMENTMETHOD_FAIL
        ],
        promise: (client: ApiClient) =>
          client.paymentMethods
            .create(nonce, postalCode)
            .then(paymentMethod => {
              resolve(paymentMethod);
              return paymentMethod;
            })
            .catch(err => {
              reject(err);
              throw err;
            }),
        nonce,
        postalCode
      });
    });
  };
}

export const toggleAllPaymentMethods = makeActionCreator(
  TOGGLE_ALL_PAYMENTMETHODS
);

export function deletePaymentMethod(id: number) {
  return {
    types: [
      DELETE_PAYMENTMETHOD,
      DELETE_PAYMENTMETHOD_SUCCESS,
      DELETE_PAYMENTMETHOD_FAIL
    ],
    promise: (client: ApiClient) =>
      client.paymentMethods.deletePaymentMethod(id),
    id
  };
}

export function createPaypalPaymentMethod(nonce: string) {
  return {
    types: [
      CREATE_PAYPAL_PAYMENTMETHOD,
      CREATE_PAYPAL_PAYMENTMETHOD_SUCCESS,
      CREATE_PAYPAL_PAYMENTMETHOD_FAIL
    ],
    promise: (client: ApiClient) => client.paymentMethods.createPaypal(nonce)
  };
}

export const updatePaymentTab = makeActionCreator(UPDATE_PAYMENT_TAB, 'index');
export const filterPaymentMethods = makeActionCreator(
  FILTER_PAYMENT_METHODS,
  'excludedPaymentMethods'
);

export const clearGlobalApiError = makeActionCreator(CLEAR_GLOBAL_API_ERROR);

export const setGooglePayAvailable = makeActionCreator(
  SET_GOOGLE_PAY_AVAILABLE
);

export const setGooglePayHasInitialised = makeActionCreator(
  SET_GOOGLE_PAY_HAS_INITIALISED
);

export const setApplePayAvailable = makeActionCreator(SET_APPLE_PAY_AVAILABLE);

export const setApplePayHasInitialised = makeActionCreator(
  SET_APPLE_PAY_HAS_INITIALISED
);

export const setInitialState = makeActionCreator(SET_INITIAL_STATE);

export const selectPaymentMethods = state => state.payments.paymentMethods;
export const selectPaymentDetailsLocked = state =>
  state.payments.paymentDetailsLocked;
